import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, Subject } from 'rxjs';
import {
  ICarouselBookableService,
  IGeomarker,
  ITopTenMarkerDetail,
  IPagination,
  IValuation,
  IQualificationMarker,
  IUserQualificationMarker,
  IValuationMarker,
  ISearchResult,
  IInfoRating,
} from '../models';
import { GeomarkerEndpoint } from '../endpoints/geomarker';
import { StringUtil } from '@shared/utils';
import { Params } from '@angular/router';

/**
 * ### **GeomarkerService**
 *
 * Un Servicio en Angular es el mecanismo para compartir funcionalidad entre componentes, en este caso se encarga de de compartir
 * las funciones hacen diferentes peticiones o llamados a distintos API's que genera el backend.Algunos componentes que hacen uso
 * de este servicio son:
 *
 *  * {@link ProfileUserResultComponent}
 *
 */
@Injectable({
  providedIn: 'root',
})
export class Geomarker {
  private readonly http = inject(HttpClient);

  /** Subject to notify changes in the ratings */
  private readonly updateRatings = new Subject<void>();

  /** Notifies changes in the ratings */
  onUpdateRatings(): void {
    this.updateRatings.next();
  }

  /** Observable to notify changes in valuations */
  getUpdateRatings$(): Observable<void> {
    return this.updateRatings.asObservable();
  }

  constructor() {}

  /**
   * Obtiene la informacion de un geomarcador
   * @param slug slug nombre clave del geomarcador que se obtendra la información
   */
  get(slug: any): Observable<IGeomarker> {
    return this.http.get<IGeomarker>(StringUtil.format(GeomarkerEndpoint.get, slug));
  }

  /**
   *  Obtiene la informacion de un geomarcador que este inactivo
   * @param slug nombre clave del geomarcador que se obtendra la información
   */
  getInactive(slug: any): Observable<IGeomarker> {
    return this.http.get<IGeomarker>(StringUtil.format(GeomarkerEndpoint.getInactive, slug));
  }

  /**
   * Funcion encargada de obtener la informacion de una seccion del top 10, además, pregunta por la comida local,
   * esto con el fin de agregar el filtro a la url.
   * @param url Parametro con la url de la petición.
   * @param local_food Parametro con el estado para la comida local.
   */
  topten(url: string, local_food: boolean): Observable<IPagination<Array<ITopTenMarkerDetail>>> {
    const uri = local_food ? `${url}?local_food=${1}` : url;
    return this.http.get<IPagination<Array<ITopTenMarkerDetail>>>(uri);
  }

  /**
   * Function responsible for fetching the ratings and qualifications information of a specific geomarker
   * @param markerId Id of the geomarker
   * @returns Returns an observable with the ratings and qualifications information
   */
  getInfoRatings(markerId: number): Observable<IInfoRating> {
    return this.http.get<IInfoRating>(
      StringUtil.format(GeomarkerEndpoint.markerInfoRatings, markerId),
    );
  }

  /**
   * Función encargada de traer las valoraciones(comentarios) hechas a un geomarcador, para esto
   * hace uso del servicio httpClient y su petición get.
   * @param marker id del geomarcador
   * @param url Parametro (opcional) que contiene la url de la petición.
   */
  getValuations(marker: number, url?: string): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      !url ? StringUtil.format(GeomarkerEndpoint.markerValuations, marker) : url,
    );
  }

  /**
   * Función encargada agregar la valoracion hecha por un usuario a un geomarcador, para esto
   * realiza una petición post al servidor.
   * @param valuation Objeto que trae la información del usuario y del geomarcador.
   */
  addValuation(valuation: IValuationMarker): Observable<IValuationMarker> {
    return this.http.post<IValuationMarker>(GeomarkerEndpoint.valuationsRest, valuation);
  }

  /**
   * Función encargada editar la valoracion hecha por un usuario a un geomarcador, para esto
   * realiza una petición put al servidor.
   * @param id Parametro que contiene el id del geomarcador
   * @param valuation Objeto que contiene los datos del usuario y la valoracion editada.
   */
  editValuation(id: number, valuation: IValuationMarker): Observable<IValuationMarker> {
    return this.http.put<IValuationMarker>(
      StringUtil.format(GeomarkerEndpoint.valuationsEdit, id),
      valuation,
    );
  }

  /**
   * Función encargada editar la valoracion hecha por un usuario a un geomarcador, para esto
   * realiza una petición patch al servidor.
   * @param id Parametro que contiene el id del geomarcador
   * @param valuation Objeto que contiene los datos del usuario y la valoracion editada.
   */
  patchValuation(id: number, valuation: Partial<IValuationMarker>): Observable<IValuationMarker> {
    return this.http.patch<IValuationMarker>(
      StringUtil.format(GeomarkerEndpoint.valuationsEdit, id),
      valuation,
    );
  }

  /**
   * Función encargada de agregar una calificación a un geomarcador realizada por un usuario,
   * para esto hace una petición post al servidor.
   * @param qualification Objeto que contiene los datos del usuario, geomarcador y calificación.
   */
  addQualification(qualification: IQualificationMarker): Observable<IQualificationMarker> {
    return this.http.post<IQualificationMarker>(GeomarkerEndpoint.qualificationsAdd, qualification);
  }

  /**
   * Función encargada de editar una calificación a un geomarcador realizada por un usuario,
   * para esto hace una petición put al servidor.
   * @param qualification Objeto que contiene los datos del usuari y calificación.
   * @param id Parametro que contiene el id del geoamrcador.
   */
  editQualification(
    id: number,
    qualification: IQualificationMarker,
  ): Observable<IQualificationMarker> {
    return this.http.put<IQualificationMarker>(
      StringUtil.format(GeomarkerEndpoint.qualificationsEdit, id),
      qualification,
    );
  }

  /**
   * Función encargada de obtener las Valoraciónes de un geomarcador realizada por un usuario,
   * para esto hace una petición get al servidor.
   * @param  marker Parametro que contiene el id del geoamrcador.
   * @param user Parametro que recibe el identificador del usuario
   */
  valuationsByUser(marker: number, user: number): Observable<Array<IUserQualificationMarker>> {
    return this.http.get<Array<IUserQualificationMarker>>(
      StringUtil.format(GeomarkerEndpoint.valuationsByUser, marker, user),
    );
  }

  /**
   * Función encargada de obtener la calificación de un geomarcador realizada por un usuario,
   * para esto hace una petición get al servidor.
   * @param  marker Parametro que contiene el id del geoamrcador.
   * @param user Parametro que recibe el identificador del usuario
   */
  qualificationByUser(marker: number, user: number): Observable<IUserQualificationMarker> {
    return this.http.get<IUserQualificationMarker>(
      StringUtil.format(GeomarkerEndpoint.qualificationByUser, marker, user),
    );
  }

  /**
   * Función encargada de eliminar una calificación de un geomarcador realizada por un usuario,
   * para esto hace una petición delete al servidor.
   * @param id Parametro que contiene el id del geoamrcador.
   */
  deleteQualification(id: number): Observable<any> {
    return this.http.delete(StringUtil.format(GeomarkerEndpoint.qualificationsEdit, id));
  }

  /**
   * Función encargada de eliminar una Valoracion para un geomarcador realizada por un usuario,
   * para esto hace una petición delete al servidor.
   * @param id Parametro que contiene el id del geoamrcador.
   */
  deleteValuation(id: number): Observable<any> {
    return this.http.delete(StringUtil.format(GeomarkerEndpoint.valuationsEdit, id));
  }

  /**
   * Función que permite obtener los servicios reservables de un geomarcador
   * @param id Id del geomarcador
   * @returns Retorna un observable con la información de los servicios reservables
   */
  getBookingServices(id: number): Observable<any> {
    return this.http.get(StringUtil.format(GeomarkerEndpoint.bookingService, id));
  }

  /**
   * Función que permite obtener los servicios reservables de un geomarcador
   * @param id Id del geomarcador
   * @returns Retorna un observable con la información de los servicios reservables
   */
  getCarouselbookingService(id: number): Observable<ICarouselBookableService[]> {
    return this.http
      .get<any>(StringUtil.format(GeomarkerEndpoint.carouselbookingService, id))
      .pipe(map((response: any) => response.results));
  }

  /**
   * Gets similar geomarkers by municipality
   * @param params Parameters to filter similar geomarkers by municipality
   * @returns Returns an observable with similar geomarkers information
   */
  getMarkersBy(params: Params): Observable<IPagination<ISearchResult[]>> {
    return this.http.get<IPagination<ISearchResult[]>>(GeomarkerEndpoint.markersBy, { params });
  }
}
